import { useState, useEffect, useCallback, useRef } from 'react'
import {
  fetchTicker,
  fetchOrderBook,
  fetchTrades,
  formatOrderBookEntry,
  TickerData,
  TradeData,
} from '../utils/krakenAPI'

interface OrderBookEntry {
  price: number
  quantity: number
  total: number
}

interface MarketData {
  ticker: TickerData | null
  bids: OrderBookEntry[]
  asks: OrderBookEntry[]
  trades: TradeData[]
  loading: boolean
  error: string | null
}

export function useKrakenMarket(
  pair: string = 'XBTUSD',
  refreshInterval: number = 5000,
) {
  const [data, setData] = useState<MarketData>({
    ticker: null,
    bids: [],
    asks: [],
    trades: [],
    loading: true,
    error: null,
  })

  // Use ref to avoid dependency cycle
  const lastTradeTimeRef = useRef<number | undefined>(undefined)

  const fetchData = useCallback(async () => {
    try {
      const [tickerData, orderBookData, tradesData] = await Promise.all([
        fetchTicker(pair),
        fetchOrderBook(pair, 100),
        fetchTrades(pair, lastTradeTimeRef.current),
      ])

      // Update last trade time for next fetch
      if (tradesData.length > 0) {
        const newestTradeTime = Math.max(...tradesData.map((t) => t.time))
        lastTradeTimeRef.current = newestTradeTime
      }

      setData((prev) => ({
        ticker: tickerData,
        bids: orderBookData.bids.map(formatOrderBookEntry),
        asks: orderBookData.asks.map(formatOrderBookEntry),
        // Merge new trades with existing, keep most recent 50
        trades: [...tradesData, ...prev.trades]
          .sort((a, b) => b.time - a.time)
          .slice(0, 50),
        loading: false,
        error: null,
      }))
    } catch (error) {
      setData((prev) => ({
        ...prev,
        loading: false,
        error:
          error instanceof Error
            ? error.message
            : 'Failed to fetch market data',
      }))
    }
  }, [pair])

  useEffect(() => {
    fetchData()
    const interval = setInterval(fetchData, refreshInterval)
    return () => clearInterval(interval)
  }, [fetchData, refreshInterval])

  return data
}
